import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const API_BASE_URL_FOR_IMAGE = process.env.REACT_APP_API_URL_FOR_IMAGE;

// Helper to get full image URL
export const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // For uploads, use backend URL without /api prefix
  if (url.startsWith('/uploads')) {
    const backendUrl = API_BASE_URL_FOR_IMAGE;

    return `${backendUrl}${url}`;
  }
  // For other relative paths, prepend API_BASE_URL
  return url.startsWith('/') ? `${API_BASE_URL_FOR_IMAGE}${url}` : `${API_BASE_URL_FOR_IMAGE}/${url}`;
};

export const getImageUrlOrLocalImage = (url) => {
  if (!url) return null;
  // For uploads, use backend URL without /api prefix
  if (url.startsWith('/uploads')) {
    const backendUrl = API_BASE_URL_FOR_IMAGE;

    return `${backendUrl}${url}`;
  }
  // For other relative paths, prepend API_BASE_URL
  return url;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('crm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('crm_token');
      localStorage.removeItem('crm_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (token, data) => api.post(`/auth/reset-password/${token}`, data),
};

// Properties
export const propertyAPI = {
  getAll: (params) => api.get('/properties', { params }),
  getOne: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
  getAnalytics: (id) => api.get(`/properties/${id}/analytics`),
  blockDates: (id, dates) => api.post(`/properties/${id}/block-dates`, { dates }),
  uploadImages: (id, formData) => api.post(`/properties/${id}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteImage: (id, imageId) => api.delete(`/properties/${id}/images/${imageId}`),
  setCoverImage: (id, imageId) => api.put(`/properties/${id}/images/${imageId}/cover`),
};

// Bookings
export const bookingAPI = {
  checkAvailability: (params) => api.get('/bookings/check-availability', { params }),
  getCalendar: (params) => api.get('/bookings/calendar', { params }),
  getAll: (params) => api.get('/bookings', { params }),
  getOne: (id) => api.get(`/bookings/${id}`),
  create: (data) => api.post('/bookings', data),
  updateStatus: (id, data) => api.put(`/bookings/${id}/status`, data),
  cancel: (id, reason) => api.put(`/bookings/${id}/cancel`, { reason }),
};

// Equipment
export const equipmentAPI = {
  getAll: (params) => api.get('/equipment', { params }),
  getOne: (id) => api.get(`/equipment/${id}`),
  create: (data) => api.post('/equipment', data),
  update: (id, data) => api.put(`/equipment/${id}`, data),
  delete: (id) => api.delete(`/equipment/${id}`),
  checkAvailability: (params) => api.get('/equipment/check-availability', { params }),
  updateStock: (id, data) => api.put(`/equipment/${id}/stock`, data),
  addMaintenance: (id, data) => api.post(`/equipment/${id}/maintenance`, data),
};

// Equipment Bookings
export const equipmentBookingAPI = {
  getAll: (params) => api.get('/equipment-bookings', { params }),
  getOne: (id) => api.get(`/equipment-bookings/${id}`),
  create: (data) => api.post('/equipment-bookings', data),
  updateStatus: (id, data) => api.put(`/equipment-bookings/${id}/status`, data),
  getOpsDashboard: () => api.get('/equipment-bookings/ops/dashboard'),
};

// CRM / Leads
export const crmAPI = {
  getAll: (params) => api.get('/crm', { params }),
  getOne: (id) => api.get(`/crm/${id}`),
  create: (data) => api.post('/crm', data),
  update: (id, data) => api.put(`/crm/${id}`, data),
  delete: (id) => api.delete(`/crm/${id}`),
  updateStage: (id, data) => api.put(`/crm/${id}/stage`, data),
  addActivity: (id, data) => api.post(`/crm/${id}/activities`, data),
  assign: (id, userId) => api.put(`/crm/${id}/assign`, { userId }),
  getPipeline: () => api.get('/crm/pipeline'),
  submitInquiry: (data) => api.post('/crm/inquire', data),
};

// Maintenance
export const maintenanceAPI = {
  getAll: (params) => api.get('/maintenance', { params }),
  getOne: (id) => api.get(`/maintenance/${id}`),
  create: (data) => api.post('/maintenance', data),
  update: (id, data) => api.put(`/maintenance/${id}`, data),
  addNote: (id, text) => api.post(`/maintenance/${id}/notes`, { text }),
  assign: (id, userId) => api.put(`/maintenance/${id}/assign`, { userId }),
};

// Reviews
export const reviewAPI = {
  getAll: (params) => api.get('/reviews', { params }),
  create: (data) => api.post('/reviews', data),
  respond: (id, response) => api.put(`/reviews/${id}/respond`, { response }),
  feature: (id, isFeatured) => api.put(`/reviews/${id}/feature`, { isFeatured }),
};

// Payments
export const paymentAPI = {
  confirmBooking: (id) => api.post(`/payments/confirm-booking/${id}`),
  confirmEquipment: (id) => api.post(`/payments/confirm-equipment/${id}`),
  refund: (id, data) => api.post(`/payments/refund/${id}`, data),
  getSummary: (params) => api.get('/payments/summary', { params }),
};

// Reports
export const reportAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
  getRevenue: (params) => api.get('/reports/revenue', { params }),
  getOccupancy: (params) => api.get('/reports/occupancy', { params }),
  getOwnerStatement: (params) => api.get('/reports/owner-statement', { params }),
};

// Notifications
export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Content
export const contentAPI = {
  getAll: (params) => api.get('/content', { params }),
  getOne: (id) => api.get(`/content/${id}`),
  create: (data) => api.post('/content', data),
  update: (id, data) => api.put(`/content/${id}`, data),
  delete: (id) => api.delete(`/content/${id}`),
};

// Users
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getOne: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Subscriptions
export const subscriptionAPI = {
  getCurrent: () => api.get('/subscriptions/current'),
  getPlans: () => api.get('/subscriptions/plans'),
  upgrade: (data) => api.post('/subscriptions/upgrade', data),
  cancel: (reason) => api.post('/subscriptions/cancel', { reason }),
};

// Tenant
export const tenantAPI = {
  getSettings: () => api.get('/tenants/settings'),
  updateSettings: (data) => api.put('/tenants/settings', data),
};

// Super Admin
export const superAdminAPI = {
  getMetrics: () => api.get('/super-admin/metrics'),
  getTenants: (params) => api.get('/super-admin/tenants', { params }),
  getTenant: (id) => api.get(`/super-admin/tenants/${id}`),
  createTenant: (data) => api.post('/super-admin/tenants', data),
  updateSubscription: (id, data) => api.put(`/super-admin/tenants/${id}/subscription`, data),
  suspendTenant: (id, reason) => api.put(`/super-admin/tenants/${id}/suspend`, { reason }),
};

// Guest
export const guestAPI = {
  getDashboard: () => api.get('/guests/dashboard'),
  getCheckIn: (bookingId) => api.get(`/guests/check-in/${bookingId}`),
  submitServiceRequest: (data) => api.post('/guests/service-request', data),
};

// Channel Manager
export const channelManagerAPI = {
  // Tenant
  getAccounts: () => api.get('/channel-manager/accounts'),
  connectChannel: (data) => api.post('/channel-manager/accounts/connect', data),
  disconnectChannel: (ota) => api.put(`/channel-manager/accounts/${ota}/disconnect`),
  testConnection: (ota) => api.get(`/channel-manager/accounts/${ota}/test`),
  getMappings: () => api.get('/channel-manager/mappings'),
  createMapping: (data) => api.post('/channel-manager/mappings', data),
  updateMapping: (id, data) => api.put(`/channel-manager/mappings/${id}`, data),
  deleteMapping: (id) => api.delete(`/channel-manager/mappings/${id}`),
  triggerSync: () => api.post('/channel-manager/sync'),
  triggerMappingSync: (id) => api.post(`/channel-manager/sync/${id}`),
  getSyncLogs: () => api.get('/channel-manager/sync/logs'),
  // Super Admin
  getAdminConfigs: () => api.get('/channel-manager/admin/configs'),
  toggleOTA: (ota, enabled) => api.put(`/channel-manager/admin/configs/${ota}/toggle`, { enabled }),
  upsertOTAConfig: (ota, data) => api.put(`/channel-manager/admin/configs/${ota}`, data),
  getSystemStats: () => api.get('/channel-manager/admin/stats'),
};

// iCal Calendar Sync
export const icalAPI = {
  generateExportUrl: (propertyId) => api.post(`/ical/property/${propertyId}/export`),
  getPropertySyncs: (propertyId) => api.get(`/ical/property/${propertyId}/syncs`),
  addImportFeed: (propertyId, data) => api.post(`/ical/property/${propertyId}/import`, data),
  removeImportFeed: (propertyId, feedId) => api.delete(`/ical/property/${propertyId}/import/${feedId}`),
  toggleSyncFeed: (propertyId, feedId, syncEnabled) => api.patch(`/ical/property/${propertyId}/import/${feedId}/toggle`, { syncEnabled }),
  syncProperty: (propertyId) => api.post(`/ical/property/${propertyId}/sync`),
};

// Calendar
export const calendarAPI = {
  getPropertyCalendar: (propertyId, params) => api.get(`/calendar/property/${propertyId}`, { params }),
  getMultiPropertyCalendar: (params) => api.get('/calendar/properties', { params }),
  blockDates: (propertyId, data) => api.post(`/calendar/property/${propertyId}/block`, data),
  unblockDates: (propertyId, data) => api.post(`/calendar/property/${propertyId}/unblock`, data),
};
